import React, { useState, useEffect, useCallback } from 'react';
import {
  ExerciseTemplateDTO,
  ExerciseTemplateSectionDTO,
  ExerciseTemplateSectionFieldDTO,
  ScoreSectionDTO,
  ScoreFieldDTO,
} from '../../services/apiClient';
import FormField from '../common/FormField';
import { showAlert } from '../../utils/sweetAlertUtils';
import SimpleAlert from '../common/SimpleAlert';

interface ExerciseTemplateScoringProps {
  template: ExerciseTemplateDTO;
  existingScoreSections?: ScoreSectionDTO[]; // Add this prop for edit mode
  onScoreDataChange?: (
    scoreSections: ScoreSectionDTO[],
    grandTotals: { score: number; percentage: number; mark: number },
  ) => void;
  error?: string; // Validation error to display
}

interface ScoreData {
  [fieldId: string]: number;
}

interface DirectScoreData {
  [sectionId: string]: number;
}

interface TimeData {
  [sectionId: string]: string;
}

const ExerciseTemplateScoring: React.FC<ExerciseTemplateScoringProps> = ({
  template,
  existingScoreSections,
  onScoreDataChange,
  error,
}) => {
  const [scoreData, setScoreData] = useState<ScoreData>({});
  const [directScoreData, setDirectScoreData] = useState<DirectScoreData>({});
  const [timeData, setTimeData] = useState<TimeData>({});
  const [isInitialized, setIsInitialized] = useState(false); // Add this state

  // Initialize data from existing score sections when in edit mode
  useEffect(() => {
    console.log(existingScoreSections)
    if (
      existingScoreSections &&
      existingScoreSections.length > 0 &&
      !isInitialized
    ) {
      const newScoreData: ScoreData = {};
      const newDirectScoreData: DirectScoreData = {};
      const newTimeData: TimeData = {};

      existingScoreSections.forEach((scoreSection) => {
        const sectionId = scoreSection.exerciseTemplateSectionId!;

        // Set time data if available
        if (scoreSection.timeValue) {
          newTimeData[sectionId] = scoreSection.timeValue;
        }

        // Check if this section has fields (field-based) or is direct score
        if (scoreSection.scoreFields && scoreSection.scoreFields.length > 0) {
          // Field-based section - populate field scores
          scoreSection.scoreFields.forEach((scoreField) => {
            const fieldId = scoreField.exerciseTemplateSectionFieldId!;
            newScoreData[fieldId] = scoreField.value || 0;
          });
        } else {
          // Direct score section - populate section score
          newDirectScoreData[sectionId] = scoreSection.sectionScore || 0;
        }
      });

      // Set all the data AND mark as initialized in the same batch
      setScoreData(newScoreData);
      setDirectScoreData(newDirectScoreData);
      setTimeData(newTimeData);
      setIsInitialized(true);
    }
  }, [existingScoreSections, isInitialized]);

  // Reset data when template changes (but not when existingScoreSections change)
  useEffect(() => {
    // Don't reset if we're in edit mode with existing data - this prevents the reset during initialization
    if (existingScoreSections && existingScoreSections.length > 0) {
      return;
    }

    // Reset when template changes - this will allow re-initialization for new templates
    setIsInitialized(false);

    // Reset data for new templates (not edit mode)
    setScoreData({});
    setDirectScoreData({});
    setTimeData({});
  }, [template.id, existingScoreSections]);

  // Memoize calculation functions - MOVE THESE BEFORE THE CALCULATION EFFECT
  const calculateTotalScore = useCallback(
    (section: ExerciseTemplateSectionDTO, scores: ScoreData) => {
      let totalScore = 0;
      section.fields?.forEach((field) => {
        const fieldScore = scores[field.id!] || 0;

        if (field.fieldType!.toLowerCase() === 'hitmiss') {
          // For HitMiss fields, use the score directly (already contains correct point value)
          totalScore += fieldScore;
        } else {
          // For Count fields, multiply count by point value
          totalScore += fieldScore * field.pointValue!;
        }
      });
      return totalScore;
    },
    [],
  );

  const calculatePercentage = useCallback(
    (section: ExerciseTemplateSectionDTO, scores: ScoreData) => {
      const totalScore = calculateTotalScore(section, scores);
      return section.maxTotal! > 0 ? (totalScore / section.maxTotal!) * 100 : 0;
    },
    [calculateTotalScore],
  );

  // Helper function to clean time input (replace commas with periods)
  const fixTime = useCallback((timeStr: string): number => {
    if (!timeStr) return 0;
    const cleanTime = timeStr.replace(/,/g, '.');
    return parseFloat(cleanTime) || 0;
  }, []);

  const calculateMark = useCallback(
    (
      section: ExerciseTemplateSectionDTO,
      score: number,
      time: string,
    ): number => {
      if (!section.shouldCalculateMark) return 0;

      // Check if this is a field-based section with Count fields and time enabled
      const hasCountFields = section.fields?.some(
        (field) => field.fieldType!.toLowerCase() === 'count',
      );
      const isFieldBased = section.fields && section.fields.length > 0;

      if (isFieldBased && hasCountFields && section.shouldCaptureTime && time) {
        // Semi-auto model: Points divided by time (points per second)
        const timeInSeconds = fixTime(time);
        if (timeInSeconds > 0 && score > 0) {
          return score / timeInSeconds;
        }
        return 0;
      }

      // For direct score sections (like shotgun 25/50/75)
      if (!isFieldBased || !section.fields || section.fields.length === 0) {
        if (section.shouldCaptureTime && time) {
          // Simple time penalty calculation for direct scores
          const timeInSeconds = fixTime(time);
          const timePenalty = Math.max(0, timeInSeconds - 60) * 0.1; // Example: penalty after 60 seconds
          return Math.max(0, score - timePenalty);
        }

        // For shotgun-style scoring (25, 50, 75), the mark is just the score itself
        // The percentage calculation handles the conversion
        return score;
      }

      return 0;
    },
    [fixTime],
  );

  const validateMaxShots = (
    section: ExerciseTemplateSectionDTO,
    fieldId: string,
    newValue: number,
    currentScores: ScoreData,
  ): { isValid: boolean; message?: string } => {
    // Calculate total shots excluding the field being changed
    let totalShots = 0;
    section.fields?.forEach((field) => {
      if (field.id === fieldId) {
        // Use the new value for the field being changed
        if (field.fieldType!.toLowerCase() === 'hitmiss') {
          totalShots += newValue > 0 ? 1 : 0;
        } else {
          totalShots += newValue;
        }
      } else {
        // Use existing values for other fields
        const fieldScore = currentScores[field.id!] || 0;
        if (field.fieldType!.toLowerCase() === 'hitmiss') {
          totalShots += fieldScore > 0 ? 1 : 0;
        } else {
          totalShots += fieldScore;
        }
      }
    });

    if (totalShots > section.maxShots!) {
      return {
        isValid: false,
        message: `You cannot select more than ${section.maxShots} shots!`,
      };
    }

    return { isValid: true };
  };

  const handleScoreChange = async (
    fieldId: string,
    value: number,
    section: ExerciseTemplateSectionDTO,
  ) => {
    // Validate max shots before allowing the change
    const validation = validateMaxShots(section, fieldId, value, scoreData);

    if (!validation.isValid) {
      // Show SweetAlert error and don't update the value
      await showAlert({
        title: 'Validation Error',
        text: validation.message!,
        icon: 'error',
        confirmButtonText: 'OK',
      });

      return; // Don't update the score
    }

    // Update the score
    setScoreData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleDirectScoreChange = (sectionId: string, value: number) => {
    setDirectScoreData((prev) => ({
      ...prev,
      [sectionId]: value,
    }));
  };

  const handleTimeChange = (sectionId: string, value: string) => {
    setTimeData((prev) => ({
      ...prev,
      [sectionId]: value,
    }));
  };

  // Calculate and send score data whenever it changes
  useEffect(() => {
    // Only send data if we have a valid template and callback
    if (!onScoreDataChange || !template.sections) {
      return;
    }

    // Simple logic: only send data when initialized OR when we don't have existing data
    const shouldSendData =
      isInitialized ||
      !existingScoreSections ||
      existingScoreSections.length === 0;

    if (!shouldSendData) {
      return;
    }

    const scoreSections: ScoreSectionDTO[] = [];
    let grandTotalScore = 0;
    let grandTotalPercentage = 0;
    let grandTotalMark = 0;
    let totalSections = 0;

    template.sections.forEach((section) => {
      const isFieldBased =
        section.fields && section.fields.length > 0 && section.rows! > 0;

      let sectionScore = 0;
      let sectionPercentage = 0;
      let sectionMark = 0;
      const sectionTime = timeData[section.id!] || '';
      const scoreFields: ScoreFieldDTO[] = [];

      // Find existing score section for this template section
      const existingScoreSection = existingScoreSections?.find(
        (ss) => ss.exerciseTemplateSectionId === section.id,
      );

      if (isFieldBased) {
        // Field-based section
        sectionScore = calculateTotalScore(section, scoreData);
        sectionPercentage = calculatePercentage(section, scoreData);

        // Create score fields for this section
        section.fields?.forEach((field) => {
          const fieldValue = scoreData[field.id!] || 0;

          // Find existing score field
          const existingScoreField = existingScoreSection?.scoreFields?.find(
            (sf) => sf.exerciseTemplateSectionFieldId === field.id,
          );

          // Always include all fields (Count and HitMiss), even if 0
          // This ensures validation can check for at least one non-zero score
          scoreFields.push({
            id: existingScoreField?.id, // Preserve existing ID if editing
            scoreSectionId: existingScoreSection?.id, // Preserve existing section ID if editing
            exerciseTemplateSectionFieldId: field.id,
            value: fieldValue,
            exerciseTemplateSectionField: field,
          });
        });
      } else {
        // Direct score section
        sectionScore = directScoreData[section.id!] || 0;
        sectionPercentage =
          section.maxTotal! > 0 ? (sectionScore / section.maxTotal!) * 100 : 0;
      }

      if (section.shouldCalculateMark) {
        sectionMark = calculateMark(section, sectionScore, sectionTime);
        grandTotalMark += sectionMark;
      }

      grandTotalScore += sectionScore;
      grandTotalPercentage += sectionPercentage;
      totalSections++;

      scoreSections.push({
        id: existingScoreSection?.id, // Preserve existing ID if editing
        scoreId: existingScoreSection?.scoreId, // Preserve existing score ID if editing
        exerciseTemplateSectionId: section.id,
        sectionScore,
        sectionPercentage,
        sectionMark: section.shouldCalculateMark ? sectionMark : undefined,
        timeValue: section.shouldCaptureTime ? sectionTime : undefined,
        exerciseTemplateSection: section,
        scoreFields,
        isDeleted: false,
      });
    });

    // Calculate average percentage across sections
    const avgPercentage =
      totalSections > 0 ? grandTotalPercentage / totalSections : 0;

    onScoreDataChange(scoreSections, {
      score: grandTotalScore,
      percentage: avgPercentage,
      mark: grandTotalMark,
    });
  }, [
    scoreData,
    directScoreData,
    timeData,
    template.sections,
    onScoreDataChange,
    calculateTotalScore,
    calculatePercentage,
    calculateMark,
    isInitialized,
  ]);
  // Removed existingScoreSections from dependencies to prevent infinite loops

  // Calculate total mark across all sections that have shouldCalculateMark = true
  const calculateTotalMark = (): number => {
    let totalMark = 0;

    template.sections?.forEach((section) => {
      if (section.shouldCalculateMark) {
        const isFieldBased =
          section.fields && section.fields.length > 0 && section.rows! > 0;

        if (isFieldBased) {
          // Field-based section
          const sectionScore = calculateTotalScore(section, scoreData);
          const sectionTime = timeData[section.id!] || '';
          const sectionMark = calculateMark(section, sectionScore, sectionTime);
          totalMark += sectionMark;
        } else {
          // Direct score section
          const sectionScore = directScoreData[section.id!] || 0;
          const sectionTime = timeData[section.id!] || '';
          const sectionMark = calculateMark(section, sectionScore, sectionTime);
          totalMark += sectionMark;
        }
      }
    });

    return totalMark;
  };

  // Check if template has multiple sections with mark calculation
  const shouldShowTotalMark = (): boolean => {
    const sectionsWithMarks =
      template.sections?.filter((section) => section.shouldCalculateMark) || [];
    return sectionsWithMarks.length > 1;
  };

  const calculateTotalShots = (
    section: ExerciseTemplateSectionDTO,
    scores: ScoreData,
  ) => {
    let totalShots = 0;
    section.fields?.forEach((field) => {
      if (field.fieldType!.toLowerCase() === 'hitmiss') {
        // For HitMiss fields, count as 1 shot if there's any value (hit or miss)
        totalShots += scores[field.id!] !== undefined ? 1 : 0;
      } else {
        // For Count fields, use the actual count
        totalShots += scores[field.id!] || 0;
      }
    });
    return totalShots;
  };

  const renderDirectScoreSection = (section: ExerciseTemplateSectionDTO) => {
    const currentScore = directScoreData[section.id!] || 0;
    const percentage =
      section.maxTotal! > 0 ? (currentScore / section.maxTotal!) * 100 : 0;
    const currentTime = timeData[section.id!] || '';
    const calculatedMark = section.shouldCalculateMark
      ? calculateMark(section, currentScore, currentTime)
      : 0;

    return (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">{section.title}</h3>

        <div className="bg-gray-50 p-4 rounded">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="text-center">
              <FormField
                id={`direct-score-${section.id}`}
                label="Total Score"
                type="number"
                value={currentScore.toString()}
                onChange={(e) =>
                  handleDirectScoreChange(
                    section.id!,
                    Number(e.target.value) || 0,
                  )
                }
                placeholder="Enter score"
              />
            </div>

            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Out of</div>
              <div className="text-2xl font-bold text-gray-800">
                {section.maxTotal}
              </div>
            </div>

            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Percentage</div>
              <div className="text-2xl font-bold text-primary">
                {percentage.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Time section */}
          {section.shouldCaptureTime && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <FormField
                  id={`time-${section.id}`}
                  label="Time (seconds)"
                  type="text"
                  value={currentTime}
                  onChange={(e) =>
                    handleTimeChange(section.id!, e.target.value)
                  }
                  placeholder="Enter time (e.g., 45.5 or 45,5)"
                />
              </div>
            </div>
          )}

          {/* Mark section */}
          {section.shouldCalculateMark && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {section.shouldCaptureTime && currentTime
                    ? 'Calculated Mark (Score ÷ Time)'
                    : 'Mark'}
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md text-center bg-gray-100 text-lg font-medium">
                  {calculatedMark.toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFieldBasedSection = (section: ExerciseTemplateSectionDTO) => {
    // Group fields by row
    const fieldsByRow: { [row: number]: ExerciseTemplateSectionFieldDTO[] } =
      {};
    section.fields?.forEach((field) => {
      if (!fieldsByRow[field.row!]) {
        fieldsByRow[field.row!] = [];
      }
      fieldsByRow[field.row!].push(field);
    });

    // Sort fields within each row by display position
    Object.keys(fieldsByRow).forEach((row) => {
      fieldsByRow[Number(row)].sort(
        (a, b) => a.displayPosition! - b.displayPosition!,
      );
    });

    const rows = Object.keys(fieldsByRow).map(Number).sort();
    const currentScore = calculateTotalScore(section, scoreData);
    const currentTime = timeData[section.id!] || '';
    const calculatedMark = section.shouldCalculateMark
      ? calculateMark(section, currentScore, currentTime)
      : 0;

    return (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">{section.title}</h3>

        {/* Table container with horizontal scroll on mobile only */}
        <div className="w-full mb-8 overflow-x-auto md:overflow-x-visible">
          <table className="min-w-full border-collapse border border-gray-300">
            <tbody>
              {rows.map((rowNumber) => (
                <React.Fragment key={rowNumber}>
                  {/* Header row for each data row */}
                  <tr className="bg-gray-50">
                    {fieldsByRow[rowNumber].map((field) => (
                      <th
                        key={`header-${field.id}`}
                        className="border border-gray-300 px-3 py-2 text-center text-sm font-medium text-gray-700 min-w-[80px]"
                      >
                        {field.title}
                      </th>
                    ))}
                  </tr>

                  {/* Data row with dropdowns */}
                  <tr>
                    {fieldsByRow[rowNumber].map((field) => (
                      <td
                        key={field.id}
                        className="border border-gray-300 px-2 py-2 text-center"
                      >
                        {field.fieldType!.toLowerCase() === 'count' ? (
                          <FormField
                            id={`field-${field.id}`}
                            label=""
                            type="dropdown"
                            value={(scoreData[field.id!] || 0).toString()}
                            onChange={(e) =>
                              handleScoreChange(
                                field.id!,
                                Number(e.target.value),
                                section,
                              )
                            }
                            options={Array.from(
                              { length: section.maxShots! + 1 },
                              (_, i) => ({
                                value: i.toString(),
                                label: i.toString(),
                              }),
                            )}
                          />
                        ) : field.fieldType!.toLowerCase() === 'hitmiss' ? (
                          <FormField
                            id={`field-${field.id}`}
                            label=""
                            type="dropdown"
                            value={(scoreData[field.id!] || 0).toString()}
                            onChange={(e) =>
                              handleScoreChange(
                                field.id!,
                                Number(e.target.value),
                                section,
                              )
                            }
                            options={[
                              { value: '0', label: 'Miss' },
                              {
                                value: field.pointValue!.toString(),
                                label: 'Hit',
                              },
                            ]}
                          />
                        ) : (
                          <FormField
                            id={`field-${field.id}`}
                            label=""
                            type="text"
                            value={(scoreData[field.id!] || '').toString()}
                            onChange={(e) =>
                              handleScoreChange(
                                field.id!,
                                Number(e.target.value) || 0,
                                section,
                              )
                            }
                            placeholder="Enter value"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Section for field-based sections */}
        <div className="mt-4 p-3 bg-gray-50 rounded">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total:</span>
                <span className="font-medium">{section.maxTotal}</span>
              </div>
            </div>
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">% Score:</span>
                <span className="font-medium">
                  {calculatePercentage(section, scoreData).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Additional totals row */}
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Score:</span>
                <span className="font-medium">{currentScore}</span>
              </div>
            </div>
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Shots:</span>
                <span
                  className={`font-medium ${calculateTotalShots(section, scoreData) > section.maxShots! ? 'text-red-600' : ''}`}
                >
                  {calculateTotalShots(section, scoreData)} / {section.maxShots}
                </span>
              </div>
            </div>
          </div>

          {/* Time section for field-based sections */}
          {section.shouldCaptureTime && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <FormField
                id={`time-${section.id}`}
                label="Time (seconds)"
                type="text"
                value={currentTime}
                onChange={(e) => handleTimeChange(section.id!, e.target.value)}
                placeholder="Enter time (e.g., 45.5 or 45,5)"
              />
            </div>
          )}

          {/* Mark section for field-based sections */}
          {section.shouldCalculateMark && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {section.shouldCaptureTime && currentTime
                      ? 'Calculated Mark (Score ÷ Time):'
                      : 'Calculated Mark:'}
                  </span>
                  <span className="font-medium text-primary">
                    {calculatedMark.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (section: ExerciseTemplateSectionDTO) => {
    // Check if section has no fields or no rows - render direct score input
    if (!section.fields || section.fields.length === 0 || section.rows === 0) {
      return renderDirectScoreSection(section);
    }

    // Otherwise render field-based section
    return renderFieldBasedSection(section);
  };

  // Render total mark summary if there are multiple sections with marks
  const renderTotalMarkSummary = () => {
    if (!shouldShowTotalMark()) return null;

    const totalMark = calculateTotalMark();
    const sectionsWithMarks =
      template.sections?.filter((section) => section.shouldCalculateMark) || [];

    return (
      <div className="mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-xl font-bold text-primary mb-4">
          Total Mark Summary
        </h3>

        <div className="space-y-2 mb-4">
          {sectionsWithMarks.map((section) => {
            const isFieldBased =
              section.fields && section.fields.length > 0 && section.rows! > 0;
            const sectionScore = isFieldBased
              ? calculateTotalScore(section, scoreData)
              : directScoreData[section.id!] || 0;
            const sectionTime = timeData[section.id!] || '';
            const sectionMark = calculateMark(
              section,
              sectionScore,
              sectionTime,
            );

            return (
              <div
                key={section.id}
                className="flex justify-between items-center py-2 border-b border-gray-200"
              >
                <span className="text-sm font-medium text-gray-700">
                  {section.title}:
                </span>
                <span className="text-sm font-bold text-primary">
                  {sectionMark.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>

        <div className="pt-3 border-t-2 border-gray-300">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-800">Total Mark:</span>
            <span className="text-2xl font-bold text-primary">
              {totalMark.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Validation Error */}
      {error && (
        <SimpleAlert variant="error" message={error} />
      )}

      {template.sections && template.sections.length > 0 ? (
        <>
          {template.sections
            .sort((a, b) => a.displayPosition! - b.displayPosition!)
            .map((section) => (
              <div key={section.id}>{renderSection(section)}</div>
            ))}

          {/* Total Mark Summary */}
          {renderTotalMarkSummary()}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No sections defined for this template.
        </div>
      )}
    </div>
  );
};

export default ExerciseTemplateScoring;
